// Typed static client for /Store/BusinessHours.
// All-static, returns typed data (never a raw AxiosResponse). Follows the PositiveIdService precedent.

import axios from 'axios'
import { BusinessHours } from '@/models/Store'

/** The trimmed store projection /Store/BusinessHours returns. */
export interface StoreBusinessHours {
    id: number
    name?: string
    active: boolean
    businessHours?: BusinessHours
}

// Shared across every screen that computes business-day dates: a weekly opening schedule changes at
// most a few times a year, so one fetch per page load is plenty. Cleared on failure so a transient
// error does not poison the rest of the session.
let storesFetchPromise: Promise<StoreBusinessHours[]> | null = null

export class StoreBusinessHoursService {

    /** Every store, ordered by id. Returns an empty list rather than throwing if the fetch fails. */
    static getAll(): Promise<StoreBusinessHours[]> {
        storesFetchPromise ??= axios.get<StoreBusinessHours[]>('/Store/BusinessHours')
            .then(resp => (resp.data ?? []).sort((a, b) => a.id - b.id))
            .catch(err => {
                storesFetchPromise = null
                console.error(err)
                return []
            })

        return storesFetchPromise
    }

    /**
     * The first configured schedule among the candidate store ids, falling back to the lowest-id
     * active store — the order TaskTemplateCloneService uses on the server, so a date previewed in
     * the browser matches the one the server computes. Undefined means nothing resolved, which
     * addBusinessDays treats as Monday-Friday.
     */
    static async resolve(...candidateStoreIds: (number | undefined | null)[]): Promise<BusinessHours | undefined> {
        // Inactive stores are excluded to match the server, which only ever sees active ones.
        const active = (await this.getAll()).filter(s => s.active)

        for (const id of candidateStoreIds) {
            if (!id) continue
            const hours = active.find(s => s.id === id)?.businessHours
            if (hours) return hours
        }

        return active[0]?.businessHours
    }
}

export default StoreBusinessHoursService
